import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { analyseChallenge, analyseChallengeFollowup } from '../api/challenges';

const createMessage = (role, content) => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
  timestamp: new Date(),
});

const POPUP_FEATURES = 'width=480,height=640,left=200,top=120,resizable=yes,scrollbars=yes';

const copyStylesToWindow = (source, target) => {
  if (!source || !target) return;
  try {
    const styleNodes = [...source.querySelectorAll('link[rel="stylesheet"], style')];
    styleNodes.forEach((node) => {
      target.head.appendChild(node.cloneNode(true));
    });

    target.documentElement.className = source.documentElement.className;
    target.body.className = source.body.className;
  } catch (error) {
    console.warn('Failed to copy styles to popout window:', error);
  }
};

const ChallengeAnalysisChat = ({ challenge, isOpen, onClose, onAssistantMessage }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [portalContainer, setPortalContainer] = useState(null);
  const initialisedRef = useRef(false);
  const prevChallengeIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const popoutWindowRef = useRef(null);
  const popoutMonitorRef = useRef(null);
  const closeCallbackRef = useRef(onClose);
  const theme = 'light';

  useEffect(() => {
    closeCallbackRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isOpen) {
      if (popoutMonitorRef.current) {
        clearInterval(popoutMonitorRef.current);
        popoutMonitorRef.current = null;
      }
      if (popoutWindowRef.current && !popoutWindowRef.current.closed) {
        popoutWindowRef.current.close();
      }
      popoutWindowRef.current = null;
      setPortalContainer(null);
      return;
    }

    if (popoutWindowRef.current && !popoutWindowRef.current.closed) {
      return;
    }

    const newWindow = window.open('', '_blank', POPUP_FEATURES);
    if (!newWindow) {
      console.error('Unable to open challenge analysis popout window.');
      return;
    }

    popoutWindowRef.current = newWindow;
    newWindow.focus();
    const { document: newDoc } = newWindow;
    newDoc.title = challenge?.id
      ? `Challenge ${challenge.id} Analysis`
      : 'Challenge Analysis';
    newDoc.body.style.margin = '0';
    copyStylesToWindow(window.document, newDoc);

    const mountNode = newDoc.createElement('div');
    newDoc.body.appendChild(mountNode);
    setPortalContainer(mountNode);

    const handleBeforeUnload = () => {
      if (closeCallbackRef.current) closeCallbackRef.current();
    };
    newWindow.addEventListener('beforeunload', handleBeforeUnload);

    popoutMonitorRef.current = window.setInterval(() => {
      if (newWindow.closed) {
        clearInterval(popoutMonitorRef.current);
        popoutMonitorRef.current = null;
        if (closeCallbackRef.current) closeCallbackRef.current();
      }
    }, 500);

    return () => {
      newWindow.removeEventListener('beforeunload', handleBeforeUnload);
      if (popoutMonitorRef.current) {
        clearInterval(popoutMonitorRef.current);
        popoutMonitorRef.current = null;
      }
      if (!newWindow.closed) {
        newWindow.close();
      }
      popoutWindowRef.current = null;
      setPortalContainer(null);
    };
  }, [isOpen]);

  useEffect(() => () => {
    if (popoutMonitorRef.current) {
      clearInterval(popoutMonitorRef.current);
      popoutMonitorRef.current = null;
    }
    if (popoutWindowRef.current && !popoutWindowRef.current.closed) {
      popoutWindowRef.current.close();
      popoutWindowRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (popoutWindowRef.current && !popoutWindowRef.current.closed) {
      popoutWindowRef.current.document.title = challenge?.id
        ? `Challenge ${challenge.id} Analysis`
        : 'Challenge Analysis';
    }
  }, [challenge?.id]);

  const analysisContext = useMemo(() => {
    if (!challenge) return {};
    const context = {};
    if (challenge.status != null) context.status = challenge.status;
    if (challenge.guidance) context.guidance = challenge.guidance;
    if (challenge.created_at) context.created_at = challenge.created_at;
    if (challenge.integration_id) context.integration_id = challenge.integration_id;
    if (challenge.workgroup_id) context.workgroup_id = challenge.workgroup_id;
    if (challenge.challenge_title) context.challenge_title = challenge.challenge_title;
    if (challenge.description) context.description = challenge.description;
    return context;
  }, [
    challenge?.status,
    challenge?.guidance,
    challenge?.created_at,
    challenge?.integration_id,
    challenge?.workgroup_id,
    challenge?.challenge_title,
    challenge?.description,
  ]);

  const handleSend = useCallback(
    async (content, options = {}) => {
      const trimmed = (content || '').trim();
      if (!trimmed || !challenge?.id) {
        return false;
      }

      const userMessage = createMessage('user', trimmed);
      let historySnapshot = [];

      setMessages((prev) => {
        const updated = [...prev, userMessage];
        historySnapshot = updated;
        return updated;
      });

      setIsLoading(true);

      const targetSessionId = Object.prototype.hasOwnProperty.call(options, 'sessionOverride')
        ? options.sessionOverride
        : sessionId;
      const normalizedSessionId = targetSessionId != null ? String(targetSessionId).trim() : '';
      const shouldUseFollowup = normalizedSessionId.length > 0;

      try {
        const response = shouldUseFollowup
          ? await analyseChallengeFollowup(
              normalizedSessionId,
              trimmed,
              historySnapshot,
              analysisContext,
              challenge?.integration_id,
            )
          : await analyseChallenge(
              challenge.id,
              trimmed,
              historySnapshot,
              analysisContext,
              challenge?.integration_id,
            );

        if (response?.session_id) {
          setSessionId(String(response.session_id));
        }

        const reply = response?.message || 'The analysis service did not return a message.';
        const assistantMessage = createMessage('assistant', reply);
        setMessages((prev) => [...prev, assistantMessage]);
        if (typeof onAssistantMessage === 'function') {
          onAssistantMessage(typeof reply === 'string' ? reply : String(reply));
        }
      } catch (error) {
        const assistantMessage = createMessage(
          'assistant',
          `I could not complete the analysis: ${error?.message || 'Unknown error.'}`,
        );
        setMessages((prev) => [...prev, assistantMessage]);
      } finally {
        setIsLoading(false);
      }

      return true;
    },
    [challenge?.id, analysisContext, sessionId, onAssistantMessage],
  );

  const handleUserSend = useCallback(
    async (content, _images = [], _files = []) => handleSend(content),
    [handleSend],
  );

  useEffect(() => {
    if (!isOpen) {
      initialisedRef.current = false;
      prevChallengeIdRef.current = null;
      setSessionId(null);
      setMessages([]);
      setIsLoading(false);
      return;
    }

    const currentChallengeId = challenge?.id ?? null;
    const hasChallengeChanged = prevChallengeIdRef.current !== currentChallengeId;

    if (hasChallengeChanged) {
      prevChallengeIdRef.current = currentChallengeId;
      initialisedRef.current = false;
      setSessionId(null);
      setMessages([]);
      setIsLoading(false);
    }

    if (isOpen && currentChallengeId && !initialisedRef.current) {
      initialisedRef.current = true;
      handleSend('Please analyse this challenge for me.', { sessionOverride: null });
    }
  }, [isOpen, challenge?.id, handleSend]);

  if (!isOpen) return null;

  if (!portalContainer) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="flex h-screen w-screen flex-col bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Challenge Analysis</h3>
          {challenge?.id && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Analysing challenge {challenge.id}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Close analysis chat"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} theme={theme} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
        <ChatInput
          onSend={handleUserSend}
          sendKey="Enter"
          isLoading={isLoading}
          onCancel={() => setIsLoading(false)}
        />
        {isLoading && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Waiting for analysis…</p>
        )}
      </div>
    </div>,
    portalContainer,
  );
};

export default ChallengeAnalysisChat;
